import { SLOTS_APIS } from "@/api_framework/api_config";
import {
  IBookingCartPreviewRequestBody,
  IBookingCartPreviewResponse,
  IBookingCartPreviewResponseData,
} from "@/api_framework/api_modals/Bookings";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { setBookingCartPreviewData } from "@/app_redux/reducers/slices/auth/slots_slice";
import axios, { AxiosError, AxiosResponse } from "axios";
import AxiosCurlirize from "axios-curlirize";
import { useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useConfiguration } from "../../useConfiguration";
import { useCurlLogger } from "../../useCurlLogger";

AxiosCurlirize(axios, () => {});

export const useBookingCartPreviewAPI = () => {
  const dispatch = useAppDispatch();
  const configuration = useConfiguration();
  const { sendCurl } = useCurlLogger();
  const [isBookingCartPreviewInProgress, setIsBookingCartPreviewInProgress] = useState(false);
  const latestPreviewRequestIdRef = useRef(0);
  // Visit time the shown preview was priced for; surge can differ per time.
  const [previewSurgeKey, setPreviewSurgeKey] = useState<string | null>(null);

  const execute = useCallback(
    async (payload: IBookingCartPreviewRequestBody) => {
      const hasCartItems = (payload?.cart_items?.length ?? 0) > 0;
      if (!payload?.user_id || !payload?.clinic_id || !hasCartItems) {
        dispatch(setBookingCartPreviewData({ data: null }));
        return null;
      }

      const requestId = latestPreviewRequestIdRef.current + 1;
      latestPreviewRequestIdRef.current = requestId;
      setIsBookingCartPreviewInProgress(true);
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        const response = await axios
          .post(SLOTS_APIS?.CART_PREVIEW?.baseURL ?? "", { source: "vms", ...payload }, {
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
          })
          .then((res: AxiosResponse<IBookingCartPreviewResponse>) => {
            if (requestId !== latestPreviewRequestIdRef.current) {
              return null;
            }
            if (res.data.status && res.data.data) {
              const previewData: IBookingCartPreviewResponseData = res.data.data;
              dispatch(setBookingCartPreviewData({ data: previewData }));
              return previewData;
            }

            dispatch(setBookingCartPreviewData({ data: null }));
            return null;
          })
          .catch((e: AxiosError) => {
            if (e.config?.["curlCommand"]) {
              sendCurl(e.config["curlCommand"]);
            }
            if (requestId !== latestPreviewRequestIdRef.current) {
              return null;
            }
            dispatch(setBookingCartPreviewData({ data: null }));
            return null;
          });

        return response;
      } catch (e: any) {
        if (requestId === latestPreviewRequestIdRef.current) {
          toast.error("Server Error: " + e.message);
          dispatch(setBookingCartPreviewData({ data: null }));
        }
        return null;
      } finally {
        if (requestId === latestPreviewRequestIdRef.current) {
          setIsBookingCartPreviewInProgress(false);
          setPreviewSurgeKey(surgeKey(payload));
        }
      }
    },
    [configuration, dispatch, sendCurl],
  );

  return {
    execute,
    isBookingCartPreviewInProgress,
    previewSurgeKey,
  };
};

type SurgeTimePayload = { slot_id?: string; surge_start?: string; surge_end?: string };

// Visit time for surge: the chosen slot, or the booking's own time for an overbooking (no slot) or a late booking.
export const surgeSlotPayload = (
  slot?: { slot_id?: string | null; start_time?: string | null; end_time?: string | null } | null,
  bookingDate?: string | null,
  lateStartTime?: string | null,
): SurgeTimePayload => {
  const payload: SurgeTimePayload = slot?.slot_id ? { slot_id: slot.slot_id } : {};
  const ownStart = lateStartTime || (slot?.slot_id ? null : slot?.start_time);
  if (bookingDate && ownStart) {
    payload.surge_start = `${bookingDate} ${ownStart}`;
    if (slot?.end_time) payload.surge_end = `${bookingDate} ${slot.end_time}`;
  }
  return payload;
};

export const surgeKey = (payload?: SurgeTimePayload | null): string =>
  `${payload?.slot_id ?? ""}|${payload?.surge_start ?? ""}|${payload?.surge_end ?? ""}`;
